/**
 * Landed (docs/ux/PEEK-AND-RESUME.md §4.1, §5): every save that lands somewhere another person (or the saver) can open shows,
 * at a glance and without leaving the screen, WHAT was saved and WHERE it went. One event model for the web and the phone;
 * the apps only render it. Neither app writes these words itself.
 *
 * | Rule | Why |
 * |---|---|
 * | L1 A save gets a Landed card iff it made or changed a record the viewer's role can open, or added to a list another person acts on | "Copied", "Sent" and errors stay plain toasts (spec §4.1) |
 * | L2 3 s for a plain save, 30 s when the card offers Undo (UNDO_SECONDS stays the rule) | Owner decision 1 (2026-10-04) |
 * | L3 A second save to the SAME destination merges ("2 on the order list") and restarts the drain, never past 10 s from the first save | A fast tapper can't keep it up forever (debate A4) |
 * | L4 A save to a DIFFERENT destination replaces the card; the older save's Undo still lives on its row | One thing in the slot |
 * | L5 `landedView` never returns a peek the role can't open; the kiosk never peeks | Spec §4.2 table; T4 |
 * | L6 The order-list line is the O7 truth with the manager's first name; the flagger is excluded (orderalert.ts planFlag). On a shared screen, no names | Owner decision 4 |
 * | L7 Nothing health-related ever produces a Landed event | T4 (privacy) |
 */
import { firstName } from "./actions.ts";
import { UNDO_SECONDS } from "./corrections.ts";
import type { Id, Role } from "./model.ts";

export type DestinationKey = "order_list" | "today_records" | "cleaning_log" | "temperature_log";
/** Pictures both apps have (web Icons.tsx and phone ui/icons.tsx share these names). */
export type LandedPicture = "cart" | "write" | "thermo" | "spray";

export type PeekScope = "all" | "own" | "readonly";

export interface Destination {
  key: DestinationKey;
  /** ≤ 16 characters: "order list", "cleaning log". */
  short: string;
  /** The peek's title. */
  title: string;
  /** Where the full page is (a peek is never in the URL). */
  route: string;
  /** "Also at: More › Order list": so the place can be learned. */
  path: string;
  picture: LandedPicture;
  /** What a manager (and owner, admin) and a staff member may see in the peek. The kiosk never peeks (L5). */
  peek: { manager: PeekScope; staff: PeekScope | null };
}

export const DESTINATIONS: Readonly<Record<DestinationKey, Destination>> = {
  order_list: { key: "order_list", short: "order list", title: "Order list", route: "/order", path: "More › Order list", picture: "cart", peek: { manager: "all", staff: "readonly" } },
  today_records: { key: "today_records", short: "today's records", title: "Today's records", route: "/#records", path: "Today › Today's records", picture: "write", peek: { manager: "all", staff: "own" } },
  cleaning_log: { key: "cleaning_log", short: "cleaning log", title: "Cleaning log", route: "/#records", path: "Today › Today's records", picture: "spray", peek: { manager: "all", staff: "own" } },
  temperature_log: { key: "temperature_log", short: "temperature log", title: "Temperature log", route: "/#records", path: "Today › Today's records", picture: "thermo", peek: { manager: "all", staff: "own" } },
};

/** L2. */
export const LANDED_PLAIN_MS = 3000;
export const LANDED_UNDO_MS = UNDO_SECONDS * 1000;
/** L3: a merged card never stays past this long after the first save. */
export const LANDED_MERGE_CAP_MS = 10_000;
/** "Held. Let go to carry on" (spec state 2). */
export const LANDED_HELD_LINE = "Held. Let go to carry on";

/** Who was told about an order-list flag (the server's planFlag result; the flagger is never in it). */
export interface LandedTold { names: string[]; count: number }

export interface LandedEvent {
  /** Unique per save. */
  id: string;
  /** ms since epoch. */
  at: number;
  /** "Toilet paper", "Walk-in 38°F", "Restroom check". */
  what: string;
  destination: DestinationKey;
  /** The row to ring in the peek (a supply key on the order list; "<kind>:<id>" in today's records). */
  recordId?: string;
  /** The save can be undone from the card (30 s card, L2). */
  undo?: boolean;
  /** Order-list flags: who gets the alert (O7). */
  told?: LandedTold;
  /** Who saved it ("Saved for Sam" on the kiosk). */
  by?: { employeeId?: Id; name: string };
  /** Line 2 when the save itself has something true to say ("Caught early. The fix is on record."); never on a shared screen. */
  note?: string;
}

export interface LandedViewer { role: Role; kiosk: boolean }

export interface LandedView {
  line1: string;
  line2: string;
  picture: LandedPicture;
  /** The peek to open, or null (kiosk, or a role with no peek). */
  peek: DestinationKey | null;
  scope: PeekScope | null;
  ms: number;
  undo: boolean;
  /** How many saves this card stands for. */
  count: number;
}

const isManager = (r: Role) => r !== "staff";

/** L5: what the viewer may see when peeking at `d`. */
export function peekScope(d: DestinationKey, v: LandedViewer): PeekScope | null {
  if (v.kiosk) return null;
  const p = DESTINATIONS[d].peek;
  return isManager(v.role) ? p.manager : p.staff;
}

/**
 * L6, the O7 line with the name. Same counts as orderalert.ts flaggerLine: 0 → nobody else here is told (it's on the list),
 * 1 → "Maya gets the alert", 2+ → "Maya and 1 more get the alert". On a shared screen: "A manager gets the alert".
 */
export function orderToldLine(t: LandedTold | undefined, v: Pick<LandedViewer, "kiosk">): string {
  const count = t?.count ?? 0;
  if (count <= 0) return "On it. Nobody else here gets the alert";
  if (v.kiosk) return count === 1 ? "On it. A manager gets the alert" : "On it. The managers get the alert";
  const first = t?.names[0] ? firstName(t.names[0]) : undefined;
  if (!first) return count === 1 ? "On it. A manager gets the alert" : `On it. ${count} managers get the alert`;
  return count === 1 ? `On it. ${first} gets the alert` : `On it. ${first} and ${count - 1} more get the alert`;
}

/** Line 2 for a record save, by role (spec §4.1 words table). Literal truth: a manager's rows are in the binder's PDF too. */
function recordLine(d: DestinationKey, v: LandedViewer): string {
  if (v.kiosk) return `In the ${DESTINATIONS[d].short}`;
  if (!isManager(v.role)) return "Your manager sees it";
  return d === "today_records" ? "The inspection binder shows it too" : `The binder's ${DESTINATIONS[d].short} shows it too`;
}

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** The card for one or more saves to the same destination (newest last). */
export function landedView(events: readonly LandedEvent[], v: LandedViewer): LandedView {
  const last = events[events.length - 1];
  if (!last) throw new Error("landedView needs at least one event");
  const d = DESTINATIONS[last.destination];
  const n = events.length;
  const undo = events.some((e) => e.undo);
  const scope = peekScope(d.key, v);
  const names = [...new Set(events.map((e) => e.what))];
  let line1: string;
  let line2: string;
  if (v.kiosk) {
    // A shared screen: credit the person by first name; never a peek, never a manager's name (owner decision 4).
    line1 = last.by?.name ? `Saved for ${firstName(last.by.name)}` : `Saved to the ${d.short}`;
    line2 = d.key === "order_list" ? orderToldLine(last.told, v) : n > 1 ? `${n} in the ${d.short}` : `${last.what} · ${recordLine(d.key, v)}`;
  } else if (n > 1) {
    line1 = d.key === "order_list" ? `${n} on the ${d.short}` : `${n} in the ${d.short}`;
    line2 = names.join(", ");
  } else {
    line1 = `${last.what} → ${d.short}`;
    line2 = last.note ?? (d.key === "order_list" ? orderToldLine(last.told, v) : recordLine(d.key, v));
  }
  return { line1: cap(line1), line2, picture: d.picture, peek: scope ? d.key : null, scope, ms: undo ? LANDED_UNDO_MS : LANDED_PLAIN_MS, undo, count: n };
}

/** The card's state between saves: which saves it shows, when the first came, when it drops. */
export interface LandedCard { events: LandedEvent[]; firstAt: number; endsAt: number }

/**
 * L3/L4. `cur` is the card showing now (or null). Same destination: merge, restart the drain, capped at 10 s from the first
 * save. Different destination (or the card already gone): a new card.
 */
export function mergeLanded(cur: LandedCard | null, next: LandedEvent, now: number): LandedCard {
  const ms = next.undo ? LANDED_UNDO_MS : LANDED_PLAIN_MS;
  if (cur && now < cur.endsAt && cur.events[0]?.destination === next.destination) {
    const events = [...cur.events.filter((e) => e.id !== next.id), next];
    // A card that offers Undo stays for the whole undo window (30 s); a plain one is capped at 10 s from the first save.
    const cap = events.some((e) => e.undo) ? Math.max(LANDED_MERGE_CAP_MS, LANDED_UNDO_MS) : LANDED_MERGE_CAP_MS;
    const endsAt = Math.min(Math.max(cur.endsAt, now + ms), cur.firstAt + cap);
    return { events, firstAt: cur.firstAt, endsAt };
  }
  return { events: [next], firstAt: now, endsAt: now + ms };
}

/** A held finger (or focus) pauses the drain: the time left is kept and starts again on release. */
export function pauseLanded(c: LandedCard, now: number): { left: number } { return { left: Math.max(0, c.endsAt - now) }; }
export function resumeLanded(c: LandedCard, left: number, now: number): LandedCard { return { ...c, endsAt: now + left }; }

/** Fraction of the drain bar left (1 → 0); no digits ever. */
export function landedLeft(c: LandedCard, now: number, total = c.endsAt - c.firstAt): number {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(1, (c.endsAt - now) / total));
}

/* ─────────────── builders: the facts a save already returns → the event ─────────────── */

let seq = 0;
const nextId = (now: number) => `l${now.toString(36)}${(seq++).toString(36)}`;

/** A supply said to be low or out, or added to the order list (stock check, supplies). `told` from the server (O7). */
export function landedOrderFlag(o: { name: string; key: string; told?: LandedTold; undo?: boolean; by?: LandedEvent["by"]; now: number }): LandedEvent {
  return { id: nextId(o.now), at: o.now, what: o.name, destination: "order_list", recordId: o.key, ...(o.told ? { told: o.told } : {}), ...(o.undo ? { undo: true } : {}), ...(o.by ? { by: o.by } : {}) };
}

/**
 * Which log a record save lands in, from what the app knows at the save: a temperature template → the temperature log, a
 * cleaning template or a cleaning check-off → the cleaning log, anything else → today's records.
 */
export function recordDestination(o: { templateId?: string; category?: string; what?: string }): DestinationKey {
  const id = (o.templateId ?? "").toLowerCase();
  const what = (o.what ?? "").toLowerCase();
  if (/temp|fridge|freezer|cooler|walk[-_ ]?in|hot[-_ ]?hold|cold[-_ ]?(hold|unit)|thermo/.test(id) || /refrigerator|freezer|cooler|temperature/.test(what)) return "temperature_log";
  if (o.category === "cleaning" || /clean|restroom|sanitiz|bathroom|mop/.test(id) || /restroom|clean|sanitiz/.test(what)) return "cleaning_log";
  return "today_records";
}

/** A check saved, a fix logged, a check-off: lands in today's records (or the named log). Health saves never call this (L7). */
export function landedRecord(o: { what: string; destination?: DestinationKey; recordId?: string; undo?: boolean; by?: LandedEvent["by"]; note?: string; now: number }): LandedEvent {
  return { id: nextId(o.now), at: o.now, what: o.what, destination: o.destination ?? "today_records", ...(o.recordId ? { recordId: o.recordId } : {}), ...(o.undo ? { undo: true } : {}), ...(o.by ? { by: o.by } : {}), ...(o.note ? { note: o.note } : {}) };
}

/** A reading outside its limit saved with its fix (corrections U3: it stays on the record): credit where it's earned (G2b). */
export const CAUGHT_EARLY_NOTE = "Caught early. The fix is on record.";

const CLEAN = /clean|restroom|sanitiz|bathroom|mop|wipe/i;
const TEMP = /°|temp|walk-?in|fridge|freezer|cooler|thermo/i;

/**
 * Today's rows for a record destination: the cleaning or temperature ones when there are some, else all of today's.
 * A daily check-in never shows in a peek (it is health-adjacent, T4; it stays in today's records, where it always was).
 */
export function recordsForDestination<R extends { kind: string; title: string; line: string }>(dest: DestinationKey, rows: readonly R[]): R[] {
  const ok = rows.filter((r) => r.kind !== "checkin");
  const pick = dest === "cleaning_log" ? ok.filter((r) => r.kind === "clean" || r.kind === "room" || CLEAN.test(r.title))
    : dest === "temperature_log" ? ok.filter((r) => TEMP.test(r.title) || TEMP.test(r.line))
    : ok;
  return pick.length ? pick : ok;
}

/** Peek rows: at most 8, the new one first and marked (spec §4.2). */
export const PEEK_ROWS = 8;
export function peekRows<T>(rows: readonly T[], isNew: (r: T) => boolean): { rows: T[]; ringed: number } {
  const i = rows.findIndex(isNew);
  if (i < 0) return { rows: rows.slice(0, PEEK_ROWS), ringed: -1 };
  return { rows: [rows[i]!, ...rows.filter((_, k) => k !== i)].slice(0, PEEK_ROWS), ringed: 0 };
}

/** The peek's head: "Peek · you're still on the stock check". */
export const peekKicker = (where: string) => `Peek · you're still on ${where}`;
